import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Brain, CheckCircle2, XCircle, Plus, Trash2, Pencil, ImagePlus, Info, BookOpen, Lightbulb, Workflow, RefreshCw, Activity, Beaker } from "lucide-react";
import api, { getFileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const MODE_OPTIONS = [
  { value: "off", label: "Off — AI QC не работает", color: "bg-zinc-500" },
  { value: "shadow", label: "Shadow — AI пишет вердикт в фоне, АД не видит", color: "bg-blue-500" },
  { value: "soft", label: "Soft — АД видит вердикт, но решает сам", color: "bg-amber-500" },
  { value: "hard", label: "Hard — AI блокирует плохие рендеры до АД", color: "bg-red-500" },
];

const emptyRubric = {
  key: "",
  label: "",
  description: "",
  weight: 1.0,
  pass_threshold: 0.7,
  scope: "global",
  service_type_id: null,
  enabled: true,
  order: 0,
};

const emptyExampleForm = {
  label: "good",
  title: "",
  scope: "global",
  service_type_id: "",
  reasons: "",
  criteria_notes: "",
  tags: "",
  file: null,
};

const DEFAULT_SETTINGS = {
  ai_qc_enabled: false,
  ai_qc_mode: "off",
  ai_qc_model: "claude-opus-5-5",
  ai_qc_max_good_examples: 5,
  ai_qc_max_bad_examples: 5,
  ai_qc_min_confidence_to_gate: 0.7,
  ai_qc_video_policy: "skip",
  ai_qc_link_policy: "skip",
  ai_qc_budget_monthly_usd: 100,
};

const AIQualityControlPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const [serviceTypes, setServiceTypes] = useState([]);

  const [rubric, setRubric] = useState([]);
  const [rubricDialogOpen, setRubricDialogOpen] = useState(false);
  const [rubricDraft, setRubricDraft] = useState(emptyRubric);

  const [examples, setExamples] = useState([]);
  const [exampleFilterLabel, setExampleFilterLabel] = useState("all");
  const [exampleDialogOpen, setExampleDialogOpen] = useState(false);
  const [exampleDraft, setExampleDraft] = useState(emptyExampleForm);
  const [uploadingExample, setUploadingExample] = useState(false);

  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [expandedRunId, setExpandedRunId] = useState(null);

  // Test-run dialog
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testFile, setTestFile] = useState(null);
  const [testBrief, setTestBrief] = useState("");
  const [testServiceTypeId, setTestServiceTypeId] = useState("");
  const [testRunning, setTestRunning] = useState(false);

  useEffect(() => {
    fetchAll();
    fetchRuns();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    // Use the dedicated /ai-qc/settings endpoint — readable + writable by
    // admin + art_director (vs /settings/integrations which is admin-only).
    const safe = async (p, fallback) => {
      try { return (await p).data ?? fallback; } catch { return fallback; }
    };
    const [sData, stData, rData, eData] = await Promise.all([
      safe(api.get("/ai-qc/settings"), { ...DEFAULT_SETTINGS }),
      safe(api.get("/service-types/list"), []),
      safe(api.get("/ai-qc/rubric"), []),
      safe(api.get("/ai-qc/examples"), []),
    ]);
    setSettings({ ...DEFAULT_SETTINGS, ...(sData || {}) });
    setServiceTypes(stData || []);
    setRubric(rData || []);
    setExamples(eData || []);
    setLoading(false);
  };

  const fetchRuns = async () => {
    try {
      setRunsLoading(true);
      const res = await api.get("/ai-qc/runs", { params: { limit: 100 } });
      setRuns(res.data || []);
    } catch {
      // non-fatal
    } finally {
      setRunsLoading(false);
    }
  };

  const retryRun = async (runId) => {
    try {
      await api.post(`/ai-qc/runs/${runId}/retry`);
      toast.success("Retry scheduled");
      setTimeout(fetchRuns, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Retry failed");
    }
  };

  const openTestDialog = () => {
    setTestFile(null);
    setTestBrief("");
    setTestServiceTypeId("");
    setTestDialogOpen(true);
  };

  const submitTestRun = async () => {
    if (!testFile) {
      toast.error("Выбери картинку");
      return;
    }
    try {
      setTestRunning(true);
      const fd = new FormData();
      fd.append("file", testFile);
      if (testBrief) fd.append("task_brief", testBrief);
      if (testServiceTypeId) fd.append("service_type_id", testServiceTypeId);
      const res = await api.post("/ai-qc/test-run", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      const verdict = res.data?.verdict || {};
      const status = res.data?.status;
      if (status === "passed") {
        toast.success(`Тест: PASS — ${verdict.overall_summary || "ok"}`);
      } else if (status === "failed") {
        toast.warning(`Тест: FAIL — ${verdict.overall_summary || "rejected"}`);
      } else {
        toast.error(`Тест: ${status}`);
      }
      setTestDialogOpen(false);
      // Show new run in list and auto-expand it
      fetchRuns();
      if (res.data?.id) setExpandedRunId(res.data.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Тест не удался");
    } finally {
      setTestRunning(false);
    }
  };

  const saveSettings = async (patch) => {
    try {
      setSavingSettings(true);
      await api.put("/ai-qc/settings", patch);
      setSettings((prev) => ({ ...prev, ...patch }));
      toast.success("Настройки сохранены");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ошибка сохранения настроек");
    } finally {
      setSavingSettings(false);
    }
  };

  // ---- Rubric ----

  const openNewRubric = () => {
    setRubricDraft(emptyRubric);
    setRubricDialogOpen(true);
  };

  const openEditRubric = (row) => {
    setRubricDraft({ ...row, service_type_id: row.service_type_id || null });
    setRubricDialogOpen(true);
  };

  const saveRubric = async () => {
    if (!rubricDraft.key || !rubricDraft.label) {
      toast.error("key и label обязательны");
      return;
    }
    try {
      const payload = {
        ...rubricDraft,
        weight: parseFloat(rubricDraft.weight) || 1.0,
        pass_threshold: parseFloat(rubricDraft.pass_threshold) || 0.7,
        order: parseInt(rubricDraft.order, 10) || 0,
        service_type_id: rubricDraft.scope === "service_type" ? rubricDraft.service_type_id : null,
      };
      if (rubricDraft.id) {
        await api.put(`/ai-qc/rubric/${rubricDraft.id}`, payload);
      } else {
        await api.post("/ai-qc/rubric", payload);
      }
      setRubricDialogOpen(false);
      fetchAll();
      toast.success("Критерий сохранён");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ошибка сохранения");
    }
  };

  const deleteRubric = async (id) => {
    if (!window.confirm("Удалить критерий?")) return;
    try {
      await api.delete(`/ai-qc/rubric/${id}`);
      fetchAll();
    } catch {
      toast.error("Ошибка удаления");
    }
  };

  // ---- Examples ----

  const filteredExamples = useMemo(() => {
    if (exampleFilterLabel === "all") return examples;
    return examples.filter((e) => e.label === exampleFilterLabel);
  }, [examples, exampleFilterLabel]);

  const openNewExample = () => {
    setExampleDraft(emptyExampleForm);
    setExampleDialogOpen(true);
  };

  const uploadExample = async () => {
    if (!exampleDraft.file) {
      toast.error("Выберите изображение");
      return;
    }
    try {
      setUploadingExample(true);
      const fd = new FormData();
      fd.append("file", exampleDraft.file);
      fd.append("label", exampleDraft.label);
      fd.append("title", exampleDraft.title || "");
      fd.append("scope", exampleDraft.scope);
      if (exampleDraft.scope === "service_type" && exampleDraft.service_type_id) {
        fd.append("service_type_id", exampleDraft.service_type_id);
      }
      fd.append("reasons", exampleDraft.reasons || "");
      fd.append("criteria_notes", exampleDraft.criteria_notes || "");
      fd.append("tags", exampleDraft.tags || "");

      await api.post("/ai-qc/examples", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setExampleDialogOpen(false);
      fetchAll();
      toast.success("Пример добавлен");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ошибка загрузки");
    } finally {
      setUploadingExample(false);
    }
  };

  const deleteExample = async (id) => {
    if (!window.confirm("Удалить пример из базы знаний?")) return;
    try {
      await api.delete(`/ai-qc/examples/${id}`);
      fetchAll();
    } catch {
      toast.error("Ошибка удаления");
    }
  };

  const toggleExampleEnabled = async (ex) => {
    try {
      await api.put(`/ai-qc/examples/${ex.id}`, { enabled: !ex.enabled });
      fetchAll();
    } catch {
      toast.error("Ошибка обновления");
    }
  };

  if (loading || !settings) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  const currentMode = MODE_OPTIONS.find((m) => m.value === settings.ai_qc_mode) || MODE_OPTIONS[0];
  const goodCount = examples.filter((e) => e.label === "good").length;
  const badCount = examples.filter((e) => e.label === "bad").length;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 flex items-center gap-2">
            <Brain className="h-6 w-6 text-orange-400" />
            AI Quality Control
          </h1>
          <p className="text-zinc-400 text-sm mt-1">
            База знаний и AI-гейт для рендеров перед Арт-директором
          </p>
        </div>
        <Badge className={`${currentMode.color} text-zinc-50`}>Режим: {settings.ai_qc_mode}</Badge>
      </div>

      {/* Help / onboarding */}
      <Card className="bg-blue-500/5 border-blue-500/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-zinc-50 text-lg flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-blue-400" />
            Как это работает — краткая инструкция
          </CardTitle>
          <CardDescription className="text-zinc-400">
            Прочти перед тем как начинать. Раздел одинаково полезен и админу, и Арт-директору.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <details className="group" open>
            <summary className="cursor-pointer text-blue-300 font-medium flex items-center gap-2 py-1">
              <Workflow className="h-4 w-4" /> 1. Общая схема — куда AI встраивается
            </summary>
            <div className="pl-6 pt-2 text-zinc-300 space-y-2">
              <p>
                Сегодня: артист загружает рендер → задача уходит в очередь Арт-директора →
                АД вручную решает approve / revision → клиент.
              </p>
              <p>
                С AI QC: артист загружает рендер →{" "}
                <span className="text-orange-300">AI проверяет по твоей rubric</span> →
                <span className="text-green-300"> passed</span> идёт к АД,{" "}
                <span className="text-red-300">failed</span> возвращается артисту с комментарием AI.
                АД смотрит только отфильтрованные работы.
              </p>
              <p className="text-zinc-400 text-xs">
                ⚠️ Сейчас это <b>Phase 1 — фундамент</b>. AI-проверка ещё не запущена. Здесь ты
                наполняешь критерии (и опционально примеры). Когда Phase 2 выйдет, режим
                переключается в <code className="text-xs bg-zinc-800 px-1 rounded">shadow</code>{" "}
                и AI начнёт работать в фоне, не блокируя АД — чтобы посмотреть насколько он
                адекватен.
              </p>
            </div>
          </details>

          <details className="group">
            <summary className="cursor-pointer text-blue-300 font-medium flex items-center gap-2 py-1">
              <Lightbulb className="h-4 w-4" /> 2. Режимы работы (off / shadow / soft / hard)
            </summary>
            <div className="pl-6 pt-2 text-zinc-300 space-y-2">
              <ul className="space-y-2">
                <li>
                  <b className="text-zinc-400">off</b> — AI не запускается вовсе. Как сейчас.
                </li>
                <li>
                  <b className="text-blue-300">shadow</b> — AI проверяет каждый рендер и пишет
                  вердикт в фоне, но очередь АД не меняется. Режим для калибровки: смотришь
                  расхождения AI vs АД и подкручиваешь rubric.
                </li>
                <li>
                  <b className="text-amber-300">soft</b> — АД видит AI-вердикт как подсказку
                  (светофор + per-criterion scores), но решает сам. Удобно когда не до конца
                  доверяешь AI.
                </li>
                <li>
                  <b className="text-red-300">hard</b> — AI-failed рендер <u>не попадает</u> к
                  АД, задача сразу уходит артисту на revision с AI-комментарием. Максимальная
                  экономия времени АД. Есть отдельная вкладка "QC-blocked" и ручной override.
                </li>
              </ul>
              <p className="text-zinc-400 text-xs">
                Рекомендация: <b>off → shadow</b> (1-2 недели) → <b>soft</b> (1-2 недели) →{" "}
                <b>hard</b>. На ошибках Claude API (нет ключа, API down) AI всегда fallback к АД —
                система не "застрянет".
              </p>
            </div>
          </details>

          <details className="group">
            <summary className="cursor-pointer text-blue-300 font-medium flex items-center gap-2 py-1">
              <Info className="h-4 w-4" /> 3. Rubric — главное что надо наполнить
            </summary>
            <div className="pl-6 pt-2 text-zinc-300 space-y-2">
              <p>
                Rubric — это{" "}
                <b>набор критериев, по которым AI оценивает рендер</b>. Каждый критерий имеет:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  <b>key</b> — машинный id (например, <code className="text-xs bg-zinc-800 px-1 rounded">composition</code>).
                </li>
                <li>
                  <b>label</b> — человеческое название ("Композиция").
                </li>
                <li>
                  <b>description</b> — подробное описание которое{" "}
                  <u>дословно идёт в промпт к AI</u>. Чем конкретнее — тем точнее оценка.
                </li>
                <li>
                  <b>weight</b> — вес (0–1). Сколько этот критерий значит в итоговом "pass/fail".
                </li>
                <li>
                  <b>pass_threshold</b> — порог прохождения (0–1). Если AI ставит ниже — критерий
                  "не пройден".
                </li>
                <li>
                  <b>scope</b> — global (на все рендеры) или service_type (только для конкретной
                  услуги, например "Interior CGI" или "Product shot").
                </li>
              </ul>
              <div className="p-3 bg-zinc-800/50 rounded text-xs text-zinc-400 mt-2">
                <b className="text-zinc-300">Примеры хороших критериев на старт:</b>
                <ul className="list-disc pl-4 mt-1 space-y-0.5">
                  <li>
                    <code className="text-xs">composition</code> — "Subject placement, rule of thirds,
                    crops, perspective. Flag obvious framing errors."
                  </li>
                  <li>
                    <code className="text-xs">lighting</code> — "Natural and physically plausible
                    lighting. No blown-out highlights, no pitch-black shadows without reason."
                  </li>
                  <li>
                    <code className="text-xs">materials</code> — "Materials read correctly — wood
                    looks like wood, metal looks like metal. No plastic-shine on matte surfaces."
                  </li>
                  <li>
                    <code className="text-xs">resolution_technical</code> — "Sharp focus, no noise,
                    no rendering artefacts, correct aspect ratio."
                  </li>
                  <li>
                    <code className="text-xs">brief_compliance</code> — "Scene matches project brief
                    (time of day, camera angle, required objects present)."
                  </li>
                </ul>
              </div>
            </div>
          </details>

          <details className="group">
            <summary className="cursor-pointer text-blue-300 font-medium flex items-center gap-2 py-1">
              <ImagePlus className="h-4 w-4" /> 4. Примеры (good/bad) — три стратегии
            </summary>
            <div className="pl-6 pt-2 text-zinc-300 space-y-2">
              <p>Есть три пути к работающему AI QC — выбирай по усилиям:</p>
              <div className="space-y-2">
                <div className="p-3 border border-green-500/30 bg-green-500/5 rounded">
                  <div className="font-medium text-green-300">A. Zero-shot (без примеров)</div>
                  <div className="text-xs text-zinc-400 mt-1">
                    Только хорошо написанная rubric, ни одного примера. AI судит по описанию
                    критериев. Стартуешь за 30 минут. Средняя точность, но быстро — годится для{" "}
                    <code>shadow</code>.
                  </div>
                </div>
                <div className="p-3 border border-amber-500/30 bg-amber-500/5 rounded">
                  <div className="font-medium text-amber-300">B. Ручной KB (эта страница)</div>
                  <div className="text-xs text-zinc-400 mt-1">
                    Загружаешь сюда 5–10 good + 5–10 bad примеров на каждый service_type. AI
                    сравнивает новый рендер с ними. Точнее, но кто-то (обычно АД) должен потратить
                    время на курирование.
                  </div>
                </div>
                <div className="p-3 border border-purple-500/30 bg-purple-500/5 rounded">
                  <div className="font-medium text-purple-300">
                    C. Auto-references (Phase 3, ещё не запущено)
                  </div>
                  <div className="text-xs text-zinc-400 mt-1">
                    Система сама использует уже{" "}
                    <b>одобренные АД версии прошлых задач</b> как good-примеры, а отправленные
                    на revision — как bad. Нулевое курирование, само-обучается. Наш запланированный
                    target state. Эта страница становится "опциональным тюнингом".
                  </div>
                </div>
              </div>
              <p className="text-zinc-400 text-xs mt-2">
                Наш рекомендованный путь: <b>начать с A (zero-shot)</b> — напиши 4-6 критериев в
                rubric, запусти shadow. Если AI ошибается — добавь 2-3 пограничных примера в B
                (сюда) для тонкой подстройки. Когда Phase 3 готова — большинство работы делает C.
              </p>
            </div>
          </details>

          <details className="group">
            <summary className="cursor-pointer text-blue-300 font-medium flex items-center gap-2 py-1">
              <Brain className="h-4 w-4" /> 5. Что делать Арт-директору
            </summary>
            <div className="pl-6 pt-2 text-zinc-300 space-y-2">
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  <b>Наполни rubric.</b> Ты лучше всех знаешь что такое "хороший рендер" у нас.
                  Достаточно 4–6 критериев на старт.
                </li>
                <li>
                  <b>Режим оставь в <code>shadow</code></b> первые 2 недели. Открывай "Recent runs"
                  (появится в Phase 2) и смотри — расходится ли AI с твоими решениями. Если да —
                  подкручиваешь description критерия.
                </li>
                <li>
                  <b>В soft режиме</b> — работаешь как обычно, AI лишь подсвечивает возможные
                  проблемы. Можешь игнорить.
                </li>
                <li>
                  <b>В hard режиме</b> — если видишь что AI ошибочно отбил хороший рендер, жми
                  Override во вкладке "QC-blocked". Это также будет использоваться как сигнал для
                  улучшения промпта.
                </li>
                <li>
                  <b>Примеры добавлять не обязательно.</b> Сначала выжми максимум из rubric.
                </li>
              </ul>
            </div>
          </details>
        </CardContent>
      </Card>

      {/* Mode + config — admin and art_director both can change via /ai-qc/settings */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-zinc-50">Режим работы</CardTitle>
          <CardDescription className="text-zinc-400">
            Прогрессивная выкатка: Off → Shadow → Soft → Hard. Переключают admin и art_director.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-zinc-300">Mode</Label>
            <Select
              value={settings.ai_qc_mode}
              onValueChange={(v) => saveSettings({ ai_qc_mode: v })}
              disabled={savingSettings}
            >
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MODE_OPTIONS.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Max good examples</Label>
              <Input
                type="number"
                min="0"
                max="20"
                value={settings.ai_qc_max_good_examples}
                onChange={(e) =>
                  setSettings((s) => ({ ...s, ai_qc_max_good_examples: parseInt(e.target.value, 10) || 0 }))
                }
                onBlur={() => saveSettings({ ai_qc_max_good_examples: settings.ai_qc_max_good_examples })}
                className="bg-zinc-800 border-zinc-700 text-zinc-50"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Max bad examples</Label>
              <Input
                type="number"
                min="0"
                max="20"
                value={settings.ai_qc_max_bad_examples}
                onChange={(e) =>
                  setSettings((s) => ({ ...s, ai_qc_max_bad_examples: parseInt(e.target.value, 10) || 0 }))
                }
                onBlur={() => saveSettings({ ai_qc_max_bad_examples: settings.ai_qc_max_bad_examples })}
                className="bg-zinc-800 border-zinc-700 text-zinc-50"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Min confidence для блока (hard)</Label>
              <Input
                type="number"
                min="0"
                max="1"
                step="0.05"
                value={settings.ai_qc_min_confidence_to_gate}
                onChange={(e) =>
                  setSettings((s) => ({ ...s, ai_qc_min_confidence_to_gate: parseFloat(e.target.value) || 0 }))
                }
                onBlur={() =>
                  saveSettings({ ai_qc_min_confidence_to_gate: settings.ai_qc_min_confidence_to_gate })
                }
                className="bg-zinc-800 border-zinc-700 text-zinc-50"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Модель</Label>
              <Input
                value={settings.ai_qc_model}
                onChange={(e) => setSettings((s) => ({ ...s, ai_qc_model: e.target.value }))}
                onBlur={() => saveSettings({ ai_qc_model: settings.ai_qc_model })}
                className="bg-zinc-800 border-zinc-700 text-zinc-50"
              />
              <p className="text-xs text-zinc-500">Claude model id, e.g. claude-opus-5-5</p>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Месячный бюджет, USD (warn only)</Label>
              <Input
                type="number"
                min="0"
                value={settings.ai_qc_budget_monthly_usd}
                onChange={(e) =>
                  setSettings((s) => ({ ...s, ai_qc_budget_monthly_usd: parseFloat(e.target.value) || 0 }))
                }
                onBlur={() => saveSettings({ ai_qc_budget_monthly_usd: settings.ai_qc_budget_monthly_usd })}
                className="bg-zinc-800 border-zinc-700 text-zinc-50"
              />
            </div>
          </div>

          <div className="p-3 bg-zinc-800/40 border border-zinc-700 rounded text-xs text-zinc-400">
            Видео и внешние ссылки (<code>/version-link</code>) в v1 пропускаются без AI-проверки — помечаются{" "}
            <span className="text-zinc-300">skipped</span> и идут сразу к Арт-директору.
          </div>
        </CardContent>
      </Card>

      {/* KB tabs */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-zinc-50">База знаний</CardTitle>
          <CardDescription className="text-zinc-400">
            Критерии оценки + примеры хороших/плохих рендеров. AI будет сравнивать новую работу с этой базой.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="rubric">
            <TabsList className="bg-zinc-800">
              <TabsTrigger value="rubric">Rubric ({rubric.length})</TabsTrigger>
              <TabsTrigger value="examples">
                Примеры ({goodCount} good / {badCount} bad)
              </TabsTrigger>
              <TabsTrigger value="runs">Прогоны ({runs.length})</TabsTrigger>
            </TabsList>

            {/* Rubric tab */}
            <TabsContent value="rubric" className="mt-4 space-y-3">
              <div className="p-3 bg-zinc-800/40 border border-zinc-700/50 rounded text-xs text-zinc-400 flex gap-2">
                <Info className="h-4 w-4 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  Rubric = критерии для AI. Описание каждого критерия идёт дословно в промпт —
                  пиши конкретно, с примерами допустимого и недопустимого. На старт достаточно
                  4-6 критериев: <code className="bg-zinc-800 px-1 rounded">composition</code>,{" "}
                  <code className="bg-zinc-800 px-1 rounded">lighting</code>,{" "}
                  <code className="bg-zinc-800 px-1 rounded">materials</code>,{" "}
                  <code className="bg-zinc-800 px-1 rounded">resolution_technical</code>,{" "}
                  <code className="bg-zinc-800 px-1 rounded">brief_compliance</code>.
                </div>
              </div>
              <div className="flex justify-between items-center">
                <p className="text-sm text-zinc-400">
                  Критерии, по которым AI оценивает рендер (0–1 на каждый).
                </p>
                <Button onClick={openNewRubric} size="sm" className="bg-orange-600 hover:bg-orange-700">
                  <Plus className="h-4 w-4 mr-1" /> Новый критерий
                </Button>
              </div>

              <div className="rounded border border-zinc-800 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-800/60 text-zinc-400">
                    <tr>
                      <th className="text-left p-2">Key</th>
                      <th className="text-left p-2">Label</th>
                      <th className="text-left p-2">Scope</th>
                      <th className="text-left p-2">Weight</th>
                      <th className="text-left p-2">Threshold</th>
                      <th className="text-left p-2">Enabled</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rubric.length === 0 && (
                      <tr>
                        <td colSpan={7} className="p-6 text-center text-zinc-500">
                          Пока нет критериев. Добавьте первый, чтобы AI знал что оценивать.
                        </td>
                      </tr>
                    )}
                    {rubric.map((r) => (
                      <tr key={r.id} className="border-t border-zinc-800 text-zinc-200">
                        <td className="p-2 font-mono text-xs">{r.key}</td>
                        <td className="p-2">{r.label}</td>
                        <td className="p-2 text-zinc-400">
                          {r.scope === "global"
                            ? "global"
                            : serviceTypes.find((st) => st.id === r.service_type_id)?.name || "service_type"}
                        </td>
                        <td className="p-2">{r.weight}</td>
                        <td className="p-2">{r.pass_threshold}</td>
                        <td className="p-2">
                          {r.enabled ? (
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                          ) : (
                            <XCircle className="h-4 w-4 text-zinc-500" />
                          )}
                        </td>
                        <td className="p-2 text-right space-x-1">
                          <Button variant="ghost" size="sm" onClick={() => openEditRubric(r)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteRubric(r.id)}>
                            <Trash2 className="h-4 w-4 text-red-400" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            {/* Examples tab */}
            <TabsContent value="examples" className="mt-4 space-y-3">
              <div className="p-3 bg-zinc-800/40 border border-zinc-700/50 rounded text-xs text-zinc-400 flex gap-2">
                <Info className="h-4 w-4 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  Опционально. Если хорошо описал rubric — можно <b>не добавлять ничего</b>, AI
                  справится. Примеры нужны только когда AI систематически ошибается на каком-то
                  типе работы. Когда выйдет Phase 3, одобренные АД версии будут подтягиваться сюда
                  автоматически — ручная загрузка станет нужна только в исключениях.
                </div>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Select value={exampleFilterLabel} onValueChange={setExampleFilterLabel}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50 w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Все</SelectItem>
                      <SelectItem value="good">Good</SelectItem>
                      <SelectItem value="bad">Bad</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-zinc-500">
                    AI возьмёт до {settings.ai_qc_max_good_examples} good и{" "}
                    {settings.ai_qc_max_bad_examples} bad на каждую проверку.
                  </p>
                </div>
                <Button onClick={openNewExample} size="sm" className="bg-orange-600 hover:bg-orange-700">
                  <ImagePlus className="h-4 w-4 mr-1" /> Добавить пример
                </Button>
              </div>

              {filteredExamples.length === 0 && (
                <div className="p-10 text-center text-zinc-500 border border-dashed border-zinc-800 rounded">
                  Пусто. Добавьте 5–10 хороших и 5–10 плохих примеров чтобы AI мог сравнивать.
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {filteredExamples.map((ex) => (
                  <div
                    key={ex.id}
                    className={`rounded border overflow-hidden bg-zinc-800/40 ${
                      ex.label === "good" ? "border-green-500/40" : "border-red-500/40"
                    } ${!ex.enabled ? "opacity-50" : ""}`}
                  >
                    <div className="aspect-square bg-zinc-900">
                      <img
                        src={getFileUrl(ex.file_id)}
                        alt={ex.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-2 space-y-1">
                      <div className="flex items-center justify-between">
                        <Badge
                          className={
                            ex.label === "good" ? "bg-green-600 text-white" : "bg-red-600 text-white"
                          }
                        >
                          {ex.label}
                        </Badge>
                        <span className="text-xs text-zinc-500">
                          {ex.scope === "global"
                            ? "global"
                            : serviceTypes.find((st) => st.id === ex.service_type_id)?.name || "service_type"}
                        </span>
                      </div>
                      <div className="text-sm text-zinc-200 truncate" title={ex.title}>
                        {ex.title}
                      </div>
                      {ex.criteria_notes && (
                        <div className="text-xs text-zinc-400 line-clamp-2">{ex.criteria_notes}</div>
                      )}
                      <div className="flex gap-1 pt-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs h-7"
                          onClick={() => toggleExampleEnabled(ex)}
                        >
                          {ex.enabled ? "Выключить" : "Включить"}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs h-7 text-red-400"
                          onClick={() => deleteExample(ex.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* Runs tab */}
            <TabsContent value="runs" className="mt-4 space-y-3">
              <div className="p-3 bg-zinc-800/40 border border-zinc-700/50 rounded text-xs text-zinc-400 flex gap-2">
                <Info className="h-4 w-4 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  История AI-проверок рендеров. В режиме <code className="bg-zinc-800 px-1 rounded">shadow</code>{" "}
                  AI запускается на каждой загруженной версии (JPG/PNG) но очередь АД не блокирует —
                  тут смотришь насколько AI адекватен. Режим переключается в настройках выше.
                </div>
              </div>
              <div className="flex justify-between items-center gap-2">
                <p className="text-sm text-zinc-400">
                  Последние {runs.length} прогонов (новые сверху).
                </p>
                <div className="flex items-center gap-2">
                  <Button onClick={openTestDialog} size="sm" className="bg-orange-600 hover:bg-orange-700">
                    <Beaker className="h-4 w-4 mr-1" />
                    Тестовый прогон
                  </Button>
                  <Button onClick={fetchRuns} size="sm" variant="ghost" disabled={runsLoading}>
                    <RefreshCw className={`h-4 w-4 mr-1 ${runsLoading ? "animate-spin" : ""}`} />
                    Обновить
                  </Button>
                </div>
              </div>

              {runs.length === 0 && (
                <div className="p-10 text-center text-zinc-500 border border-dashed border-zinc-800 rounded">
                  Прогонов ещё нет. Можешь жмякнуть{" "}
                  <span className="text-orange-400">Тестовый прогон</span> и загрузить любую картинку,{" "}
                  или переключи режим в <code>shadow</code> — тогда каждая версия в задачах будет
                  автоматом проверяться.
                </div>
              )}

              <div className="space-y-2">
                {runs.map((run) => {
                  const v = run.verdict || {};
                  const isOpen = expandedRunId === run.id;
                  const statusColor =
                    run.status === "passed"
                      ? "bg-green-600"
                      : run.status === "failed"
                      ? "bg-red-600"
                      : run.status === "error"
                      ? "bg-amber-600"
                      : run.status === "skipped"
                      ? "bg-zinc-600"
                      : "bg-blue-600";
                  return (
                    <div
                      key={run.id}
                      className="border border-zinc-800 rounded bg-zinc-800/30"
                    >
                      <div className="flex items-center gap-3 p-3">
                        <Badge className={`${statusColor} text-zinc-50`}>{run.status}</Badge>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-zinc-200 truncate">
                            {v.overall_summary || run.error || run.skip_reason || "—"}
                          </div>
                          <div className="text-xs text-zinc-500 flex items-center gap-3 mt-0.5">
                            <span>v{run.version_number}</span>
                            <span>
                              task:{" "}
                              <a
                                href={`/tasks/${run.task_id}`}
                                className="text-blue-400 hover:underline"
                                title={run.task_id}
                              >
                                {run.task_id?.slice(0, 8)}
                              </a>
                            </span>
                            <span>mode: {run.mode_at_run}</span>
                            {run.duration_ms ? <span>{Math.round(run.duration_ms / 100) / 10}s</span> : null}
                            {run.cost_usd_estimate ? <span>${run.cost_usd_estimate}</span> : null}
                            {typeof v.overall_confidence === "number" && (
                              <span>conf: {Math.round(v.overall_confidence * 100)}%</span>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setExpandedRunId(isOpen ? null : run.id)}
                        >
                          <Activity className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => retryRun(run.id)}>
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                      </div>
                      {isOpen && (
                        <div className="border-t border-zinc-800 p-3 space-y-3 text-sm">
                          {run.error && (
                            <div className="text-red-400 text-xs bg-red-500/10 p-2 rounded">
                              Error: {run.error}
                            </div>
                          )}
                          {Array.isArray(v.criteria) && v.criteria.length > 0 && (
                            <div>
                              <div className="text-zinc-400 text-xs mb-1">Критерии:</div>
                              <div className="space-y-1">
                                {v.criteria.map((c, idx) => (
                                  <div
                                    key={idx}
                                    className="flex items-center gap-2 bg-zinc-800/50 p-2 rounded text-xs"
                                  >
                                    <span className="font-mono w-40 truncate text-zinc-300">
                                      {c.key}
                                    </span>
                                    <div className="flex-1 h-2 bg-zinc-700 rounded overflow-hidden">
                                      <div
                                        className={`h-full ${
                                          c.pass ? "bg-green-500" : "bg-red-500"
                                        }`}
                                        style={{
                                          width: `${Math.round((c.score || 0) * 100)}%`,
                                        }}
                                      />
                                    </div>
                                    <span className="w-10 text-right text-zinc-300">
                                      {(c.score || 0).toFixed(2)}
                                    </span>
                                    {c.notes && (
                                      <span
                                        className="flex-1 text-zinc-500 truncate"
                                        title={c.notes}
                                      >
                                        {c.notes}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {Array.isArray(v.issues) && v.issues.length > 0 && (
                            <div>
                              <div className="text-zinc-400 text-xs mb-1">Issues:</div>
                              <ul className="space-y-1 text-xs text-zinc-300">
                                {v.issues.map((iss, idx) => (
                                  <li key={idx} className="bg-zinc-800/50 p-2 rounded">
                                    <span
                                      className={`inline-block text-[10px] px-1.5 py-0.5 rounded mr-2 ${
                                        iss.severity === "high"
                                          ? "bg-red-600"
                                          : iss.severity === "med"
                                          ? "bg-amber-600"
                                          : "bg-zinc-600"
                                      } text-zinc-50`}
                                    >
                                      {iss.severity || "—"}
                                    </span>
                                    <b>{iss.area}:</b> {iss.text}
                                    {iss.suggested_fix && (
                                      <div className="text-zinc-500 mt-0.5">
                                        → {iss.suggested_fix}
                                      </div>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {run.prompt_tokens ? (
                            <div className="text-xs text-zinc-500">
                              tokens: {run.prompt_tokens} prompt / {run.completion_tokens} completion
                              &nbsp;·&nbsp; matched: {run.matched_good_count || 0} good,{" "}
                              {run.matched_bad_count || 0} bad &nbsp;·&nbsp; model: {run.model}
                            </div>
                          ) : null}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Rubric dialog */}
      <Dialog open={rubricDialogOpen} onOpenChange={setRubricDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-lg">
          <DialogHeader>
            <DialogTitle>{rubricDraft.id ? "Редактировать критерий" : "Новый критерий"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Key (id)</Label>
                <Input
                  value={rubricDraft.key}
                  onChange={(e) => setRubricDraft((d) => ({ ...d, key: e.target.value }))}
                  placeholder="composition"
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div className="space-y-1">
                <Label>Label</Label>
                <Input
                  value={rubricDraft.label}
                  onChange={(e) => setRubricDraft((d) => ({ ...d, label: e.target.value }))}
                  placeholder="Композиция"
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Описание (идёт в промпт)</Label>
              <Textarea
                rows={3}
                value={rubricDraft.description}
                onChange={(e) => setRubricDraft((d) => ({ ...d, description: e.target.value }))}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>Weight</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={rubricDraft.weight}
                  onChange={(e) => setRubricDraft((d) => ({ ...d, weight: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div className="space-y-1">
                <Label>Pass threshold</Label>
                <Input
                  type="number"
                  step="0.05"
                  min="0"
                  max="1"
                  value={rubricDraft.pass_threshold}
                  onChange={(e) => setRubricDraft((d) => ({ ...d, pass_threshold: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div className="space-y-1">
                <Label>Order</Label>
                <Input
                  type="number"
                  value={rubricDraft.order}
                  onChange={(e) => setRubricDraft((d) => ({ ...d, order: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Scope</Label>
                <Select
                  value={rubricDraft.scope}
                  onValueChange={(v) => setRubricDraft((d) => ({ ...d, scope: v }))}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="global">Global</SelectItem>
                    <SelectItem value="service_type">Service type</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {rubricDraft.scope === "service_type" && (
                <div className="space-y-1">
                  <Label>Service type</Label>
                  <Select
                    value={rubricDraft.service_type_id || ""}
                    onValueChange={(v) => setRubricDraft((d) => ({ ...d, service_type_id: v }))}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue placeholder="Выберите" />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceTypes.map((st) => (
                        <SelectItem key={st.id} value={st.id}>
                          {st.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={rubricDraft.enabled}
                onCheckedChange={(v) => setRubricDraft((d) => ({ ...d, enabled: v }))}
              />
              <Label>Активен</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRubricDialogOpen(false)}>
              Отмена
            </Button>
            <Button onClick={saveRubric} className="bg-orange-600 hover:bg-orange-700">
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Example dialog */}
      <Dialog open={exampleDialogOpen} onOpenChange={setExampleDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-lg">
          <DialogHeader>
            <DialogTitle>Новый пример в базе знаний</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Изображение (JPG/PNG)</Label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => setExampleDraft((d) => ({ ...d, file: e.target.files?.[0] || null }))}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Label</Label>
                <Select
                  value={exampleDraft.label}
                  onValueChange={(v) => setExampleDraft((d) => ({ ...d, label: v }))}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="good">Good (принимаем)</SelectItem>
                    <SelectItem value="bad">Bad (отклоняем)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Scope</Label>
                <Select
                  value={exampleDraft.scope}
                  onValueChange={(v) => setExampleDraft((d) => ({ ...d, scope: v }))}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="global">Global</SelectItem>
                    <SelectItem value="service_type">Service type</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {exampleDraft.scope === "service_type" && (
              <div className="space-y-1">
                <Label>Service type</Label>
                <Select
                  value={exampleDraft.service_type_id}
                  onValueChange={(v) => setExampleDraft((d) => ({ ...d, service_type_id: v }))}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue placeholder="Выберите" />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceTypes.map((st) => (
                      <SelectItem key={st.id} value={st.id}>
                        {st.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1">
              <Label>Название (опционально)</Label>
              <Input
                value={exampleDraft.title}
                onChange={(e) => setExampleDraft((d) => ({ ...d, title: e.target.value }))}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div className="space-y-1">
              <Label>Причины (через запятую — фигурируют в промпте)</Label>
              <Input
                value={exampleDraft.reasons}
                onChange={(e) => setExampleDraft((d) => ({ ...d, reasons: e.target.value }))}
                placeholder="composition, lighting, materials"
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div className="space-y-1">
              <Label>Что именно хорошо / плохо (полный текст)</Label>
              <Textarea
                rows={3}
                value={exampleDraft.criteria_notes}
                onChange={(e) => setExampleDraft((d) => ({ ...d, criteria_notes: e.target.value }))}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div className="space-y-1">
              <Label>Теги (через запятую)</Label>
              <Input
                value={exampleDraft.tags}
                onChange={(e) => setExampleDraft((d) => ({ ...d, tags: e.target.value }))}
                placeholder="interior, daylight, close-up"
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setExampleDialogOpen(false)}>
              Отмена
            </Button>
            <Button
              onClick={uploadExample}
              disabled={uploadingExample}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {uploadingExample ? "Загрузка..." : "Добавить"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test-run dialog */}
      <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Beaker className="h-5 w-5 text-orange-400" />
              Тестовый прогон
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-zinc-400">
              Загрузи любую картинку (JPG/PNG) — AI прогонит её через текущий rubric и базу примеров и вернёт
              вердикт. Прогон сохранится в истории как обычный, но не привязан ни к одной задаче.
              Один прогон ≈ $0.01–0.04 за вызов GPT-4o.
            </p>
            <div className="space-y-1">
              <Label>Изображение</Label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => setTestFile(e.target.files?.[0] || null)}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div className="space-y-1">
              <Label>Бриф (опционально, поможет AI оценить brief_compliance)</Label>
              <Textarea
                rows={3}
                placeholder="Например: Маркетинговый рендер интерьера гостиной, дневной свет, минимализм, бежево-серая палитра..."
                value={testBrief}
                onChange={(e) => setTestBrief(e.target.value)}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div className="space-y-1">
              <Label>Service type (опционально)</Label>
              <Select value={testServiceTypeId} onValueChange={setTestServiceTypeId}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue placeholder="Глобальные критерии" />
                </SelectTrigger>
                <SelectContent>
                  {serviceTypes.map((st) => (
                    <SelectItem key={st.id} value={st.id}>
                      {st.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-zinc-500">
                Если выберешь — AI применит специфичные критерии этого типа услуги (если они есть).
              </p>
            </div>
            {testRunning && (
              <div className="p-2 bg-blue-500/5 border border-blue-500/20 rounded text-xs text-blue-300 flex items-center gap-2">
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                AI анализирует картинку — обычно 5–15 секунд. Не закрывай окно.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTestDialogOpen(false)} disabled={testRunning}>
              Отмена
            </Button>
            <Button onClick={submitTestRun} disabled={!testFile || testRunning} className="bg-orange-600 hover:bg-orange-700">
              {testRunning ? "Прогоняем..." : "Запустить"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AIQualityControlPage;
